/**
 * Shared multi-account config resolution for AgentVault OpenClaw plugin.
 *
 * Supports two config shapes:
 * 1. Legacy single-agent: channels.agentvault.dataDir (returns ["default"])
 * 2. Multi-agent: channels.agentvault.accounts.{id}.dataDir (returns account keys)
 *
 * When `accounts` key is present, it takes precedence over top-level dataDir.
 */

const DEFAULT_API_URL = "https://api.agentvault.chat";
const DEFAULT_HTTP_PORT = 18790;

export interface ResolvedAccount {
  accountId: string;
  dataDir: string;
  apiUrl: string;
  agentName: string;
  httpPort: number;
  configured: boolean;
}

export function listAccountIds(cfg: any): string[] {
  const av = cfg?.channels?.agentvault;
  if (!av) return [];
  if (av.accounts && typeof av.accounts === "object") {
    return Object.keys(av.accounts);
  }
  return av.dataDir ? ["default"] : [];
}

export function resolveAccount(cfg: any, accountId?: string): ResolvedAccount {
  const av = cfg?.channels?.agentvault ?? {};
  const id = accountId ?? "default";

  if (av.accounts && typeof av.accounts === "object") {
    const acct = av.accounts[id];
    if (!acct) {
      return {
        accountId: id,
        dataDir: "",
        apiUrl: av.apiUrl ?? DEFAULT_API_URL,
        agentName: id,
        httpPort: DEFAULT_HTTP_PORT,
        configured: false,
      };
    }

    let httpPort = acct.httpPort;
    if (httpPort == null) {
      const keys = Object.keys(av.accounts);
      const index = keys.indexOf(id);
      httpPort = DEFAULT_HTTP_PORT + (index >= 0 ? index : 0);
    }

    return {
      accountId: id,
      dataDir: acct.dataDir ?? "",
      apiUrl: acct.apiUrl ?? av.apiUrl ?? DEFAULT_API_URL,
      agentName: acct.agentName ?? id,
      httpPort,
      configured: Boolean(acct.dataDir),
    };
  }

  const legacyIdentityMatches = id === "default" ||
    (typeof av.agentName === "string" && av.agentName === id);
  if (accountId && av.dataDir && !legacyIdentityMatches) {
    return {
      accountId: id,
      dataDir: "",
      apiUrl: av.apiUrl ?? DEFAULT_API_URL,
      agentName: id,
      httpPort: av.httpPort ?? DEFAULT_HTTP_PORT,
      configured: false,
    };
  }

  return {
    accountId: id,
    dataDir: av.dataDir ?? "~/.openclaw/agentvault",
    apiUrl: av.apiUrl ?? DEFAULT_API_URL,
    agentName: av.agentName ?? "OpenClaw Agent",
    httpPort: av.httpPort ?? DEFAULT_HTTP_PORT,
    configured: Boolean(av.dataDir),
  };
}

/**
 * The local HTTP port of ONE agent, for CLI commands that talk to it
 * (`status`, `send`). `create` gives each agent its own port, so the old
 * fixed default reached the wrong agent or nothing at all. With several
 * agents and no `accountId` there is no right guess, so say which exist.
 */
export function agentPortFromConfig(
  cfg: any,
  accountId: string | undefined,
): { port: number; accountId?: string } | { error: string } {
  const accounts = cfg?.channels?.agentvault?.accounts;
  if (!accounts || typeof accounts !== "object") {
    return { port: resolveAccount(cfg, accountId).httpPort };
  }
  const ids = Object.keys(accounts);
  if (accountId !== undefined) {
    if (!ids.includes(accountId)) {
      return {
        error: `No AgentVault agent named "${accountId}" on this computer. Agents here: ${ids.join(", ") || "none"}.`,
      };
    }
    return { port: resolveAccount(cfg, accountId).httpPort, accountId };
  }
  if (ids.length === 1) {
    return { port: resolveAccount(cfg, ids[0]).httpPort, accountId: ids[0] };
  }
  if (ids.length === 0) return { port: DEFAULT_HTTP_PORT };
  return {
    error: `This computer has several AgentVault agents (${ids.join(", ")}). Say which one: --account-id=<name>`,
  };
}
